import { forwardRef, useEffect, useRef } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { IconButton } from '~/components/IconButton';
import { Label } from '~/components/Label/Label';
import { Separator } from '~/components/Separator';

import { ArtistOnRosterFilter } from './ArtistOnRosterFilter/ArtistOnRosterFilter';
import { DEFAULT_ARTIST_FILTERS } from './constants';
import { LabelFilter } from './LabelFilter/LabelFilter';
import type { RosterFilters } from './types';

export interface ArtistFilterPanelProps {
  originalFilters: RosterFilters;
  onClose?: () => void;
  onApplyFilters: (filters: RosterFilters) => void;
}

export const ArtistFilterPanel = forwardRef<
  HTMLFormElement,
  ArtistFilterPanelProps
>(({ originalFilters, onClose, onApplyFilters }, ref) => {
  const { t } = useTranslation('roster');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const methods = useForm<RosterFilters>({
    defaultValues: structuredClone(originalFilters),
  });
  const {
    formState: { isDirty },
    reset,
    handleSubmit,
    watch,
  } = methods;

  // Focus inside the Filters panel after it opens.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [closeButtonRef]);

  const onSubmit: SubmitHandler<RosterFilters> = (formData) => {
    onApplyFilters(formData);
    onClose?.();
  };

  const handleClearFiltersButtonClick = () =>
    reset(DEFAULT_ARTIST_FILTERS, { keepDefaultValues: true });

  const isClearFiltersButtonDisabled =
    JSON.stringify(watch()) === JSON.stringify(DEFAULT_ARTIST_FILTERS);

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className="flex basis-full flex-col"
      >
        <div className="flex justify-between p-lg pb-md">
          <div className="text-headline-md font-bold">
            {t('filters.heading')}
          </div>
          <IconButton
            ref={closeButtonRef}
            type="button"
            iconType="xmark"
            iconVariant="regular"
            size="large"
            onClick={onClose}
            className="-mr-sm -mt-sm duration-300 ease-out"
            aria-label={t('filters.closePanel')}
          />
        </div>
        <div className="grow overflow-y-scroll px-lg py-md">
          <div className="flex flex-col gap-lg">
            <section className="flex flex-col gap-sm">
              <Label className="font-semibold">
                {t('filters.labelRegion')}
              </Label>
              <LabelFilter originalLabels={originalFilters.labels} />
            </section>
            <Separator />
            {/* @TODO WMG1-572 - Put back in once back end data is fixed. */}
            {/* <section>
              <Label className="font-semibold">{t('filters.artistType')}</Label>
              <ArtistCrossoverFilter />
            </section>
            <Separator /> */}
            <section>
              <Label className="font-semibold">{t('filters.status')}</Label>
              <ArtistOnRosterFilter />
            </section>
          </div>
        </div>
        <div className="flex justify-end gap-lg border-t-px px-lg py-md bg-level-03 border-emphasis-subtle">
          <Button
            size="large"
            variant="tertiary"
            onClick={handleClearFiltersButtonClick}
            disabled={isClearFiltersButtonDisabled}
          >
            {t('filters.clearFilters')}
          </Button>
          <Button size="large" disabled={!isDirty} type="submit">
            {t('filters.apply')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
});

ArtistFilterPanel.displayName = 'ArtistFilterPanel';
